import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { filter } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import {
  Container,
  Typography,
  Divider,
  Modal,
  Box,
  FormControl,
  InputLabel,
  Input,
  FormHelperText,
  TextField,
  Stack,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button
} from '@mui/material';
import Draggable, { DraggableCore } from 'react-draggable';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import jaLocale from 'date-fns/locale/ja';
import { LoadingButton, DesktopDatePicker } from '@mui/lab';
import moment from 'moment';

// components
import Scrollbar from '../../components/Scrollbar';
//

// ----------------------------------------------------------------------

export default function ReplenisherModal({ targetId = 0, open, onClose, updateDt, reload }) {
  const { SystemLib } = window;
  const navigate = useNavigate();

  useEffect(() => {}, []);

  useEffect(() => {
    if (targetId > 0) {
      getItem(targetId);
    } else {
      formik.resetForm();
    }
  }, [targetId, updateDt]);

  const getItem = async (id) => {
    const {
      data: { result, data }
    } = await SystemLib.api(`replenisher/${id}`, {}, 'get');
    if (result) {
      formik.setValues({
        // class: data.xxx,
        code: data.code,
        name: data.name,
        representative: data.representative,
        tel: data.tel,
        email: data.email,
        entry_dt: moment(data.entry_dt).toDate()
      });
    }
  };

  const yupNum = Yup.number()
    .typeError('数字を入力してください')
    .integer('整数を入力してください')
    .min(0, '0以上の数字を入れてください')
    .nullable()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value));
  const FormSchema = Yup.object().shape({
    name: Yup.string().required('法人名は必須です'),
    representative: Yup.string().required('代表者名は必須です'),
    tel: Yup.string().required('電話番号は必須です'),
    email: Yup.string()
      .email('メールアドレスの形式をご確認ください')
      .required('メールアドレスは必須です'),
    entry_dt: Yup.date().required('登録日は必須です')
  });

  const formik = useFormik({
    initialValues: {
      code: '',
      name: '',
      representative: '',
      tel: '',
      email: '',
      entry_dt: moment().toDate()
    },
    validationSchema: FormSchema,
    onSubmit: async () => {
      const { data } = await SystemLib.api(
        `replenisher${targetId === 0 ? '' : `/${targetId}`}`,
        {
          code: formik.values.code,
          name: formik.values.name,
          representative: formik.values.representative,
          tel: formik.values.tel,
          email: formik.values.email,
          entry_dt: moment(formik.values.entry_dt).format('YYYY-MM-DD')
        },
        targetId === 0 ? 'post' : 'put'
      );
      if (data.result) {
        reload(data.data.id);
        onClose();
      } else {
        alert('登録に失敗しました');
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, isValid } = formik;

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={jaLocale}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Draggable
              cancel="input"
              defaultPosition={{ x: 0, y: 0 }}
              positionOffset={{ x: '-50%', y: '-50%' }}
            >
              <Box sx={styles.box} spacing={2} maxHeight="80%" overflow="scroll">
                {/* <Stack direction="row" width="100%" mb={2}> */}
                <Stack direction="column" spacing={2} mt={1} mr={2} width="100%">
                  {targetId > 0 && (
                    <TextField
                      id="code"
                      label="コード"
                      variant="outlined"
                      value={formik.values.code}
                      disabled
                    />
                  )}
                  <DesktopDatePicker
                    id="entry_dt"
                    label="登録日"
                    inputFormat="yyyy-MM-dd"
                    {...getFieldProps('entry_dt')}
                    value={formik.values.entry_dt}
                    onChange={(v) => {
                      formik.setFieldValue('entry_dt', moment(v).toDate());
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        helperText={errors.entry_dt ? '登録日をご確認ください' : null}
                      />
                    )}
                  />
                  <TextField
                    id="name"
                    label="業者名"
                    variant="outlined"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                  />
                  <TextField
                    id="representative"
                    label="管理者名"
                    variant="outlined"
                    {...getFieldProps('representative')}
                    error={Boolean(touched.representative && errors.representative)}
                    helperText={touched.representative && errors.representative}
                    onChange={formik.handleChange}
                    value={formik.values.representative}
                  />
                  <TextField
                    id="tel"
                    label="電話番号"
                    variant="outlined"
                    {...getFieldProps('tel')}
                    error={Boolean(touched.tel && errors.tel)}
                    helperText={touched.tel && errors.tel}
                    onChange={formik.handleChange}
                    value={formik.values.tel}
                  />
                  <TextField
                    id="email"
                    label="メールアドレス"
                    variant="outlined"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                  />
                </Stack>
                {/* </Stack> */}
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={!isValid}
                  sx={{ marginTop: 3 }}
                >
                  {`${targetId === 0 ? '登録' : '更新'}する`}
                </LoadingButton>
              </Box>
            </Draggable>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </Modal>
  );
}

const styles = {
  box: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'rgb(96,169,91)',
    borderRadius: 1,
    boxShadow: 24,
    p: 4
  }
};
